/**
 * We are loading the segment snippet asynchronously depending upon webAppConfig
 * settings. This means we could potentially try and use Segment before it is
 * loaded. I have seen this with the 'identify' call since we trigger that on
 * app load if the user is logged in. See fetchUserDataStartAsync in
 * user.actions.
 *
 * The code below wraps window.segment with a promise that will wait up to
 * config.maxTime for window.segment to be defined. It will check at a defined
 * interval to see if window.segment is defined. If it becomes defined, we will
 * execute all queued calls to segment. If it does not become defined after
 * config.maxTime, we return a 'fake' segment implementation.
 */

import { getAttributionParams } from 'util/attributionParams';

let analyticsPromise;

const fakeSegment = {
  identify: () => {},
  track: () => {},
  page: () => {},
  group: () => {},
  alias: () => {},
  user: () => {},
};

const getSegment = config => {
  if (!analyticsPromise) {
    let intervalId;
    let intervalCount = 0;

    analyticsPromise = new Promise(resolve => {
      intervalId = setInterval(() => {
        ++intervalCount;
        if (!!window.analytics) {
          window.analytics.ready(() => {
            resolve(window.analytics);
          });
          clearInterval(intervalId);
        } else if (intervalCount * config.intervalDuration > config.maxTime) {
          resolve(fakeSegment);
          clearInterval(intervalId);
        }
      }, config.intervalDuration);
    });
  }

  return analyticsPromise;
};

const withAttribution = (obj = {}) => {
  const attribution = getAttributionParams();
  if (Object.keys(attribution).length === 0) return obj;
  return { ...obj, attribution };
};

export const segment = {
  config: {
    intervalDuration: 100,
    maxTime: 6000,
  },

  identify: async (...args) =>
    (await getSegment(segment.config)).identify(...args),
  track: async (event, properties = {}, ...rest) =>
    (await getSegment(segment.config)).track(
      event,
      withAttribution(properties),
      ...rest,
    ),
  page: async (...args) => (await getSegment(segment.config)).page(...args),
  group: async (...args) => (await getSegment(segment.config)).group(...args),
  alias: async (...args) => (await getSegment(segment.config)).alias(...args),

  /**
   * The group id Segment currently has stored, i.e. the client that owns the
   * identity in this browser. Reading is safe: group id is only written when
   * id() is called with an argument.
   */
  getGroupId: async () =>
    (await getSegment(segment.config)).group?.()?.id?.() ?? null,

  /**
   * Clears the stored user id and traits, leaving the anonymousId and the
   * group alone. Segment resets the anonymousId when an id changes to a
   * different non-null value, so passing null deliberately sidesteps that
   * guard — anonymous continuity survives, including in other tabs, which
   * share these cookies.
   */
  clearUserIdentity: async () => {
    const user = (await getSegment(segment.config)).user?.();
    user?.id?.(null);
    user?.traits?.({});
  },
  reset: () => {
    analyticsPromise = undefined;
    segment.config = {
      intervalDuration: 100,
      maxTime: 6000,
    };
  },
};
